import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [copiedEth, setCopiedEth] = useState(false);
  const [copiedTron, setCopiedTron] = useState(false);

  const ethWalletAddress = '0x23505fa8bfd35515fb941c3171b02de0fcc1f8e9';
  const tronWalletAddress = 'TVGSTYgaeBuPWngMZn8WDNK9j9KsZ6z4UV';

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToContract = () => {
    navigate('/contract-offer-page');
  };

  const copyToClipboard = async (address: string, setterFunction: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(address);
      setterFunction(true);
      setTimeout(() => setterFunction(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-foreground text-background py-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-content mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 text-background/80 hover:text-background transition-colors"
            >
              <Icon name="Home" size={20} />
              <span className="text-sm">На главную</span>
            </button>
            <h1 className="text-2xl font-bold">Оплата</h1>
            <div className="w-28"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Security Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-6 py-3 rounded-full border border-green-200">
            <Icon name="Shield" size={20} />
            <span className="font-semibold">Защищенная транзакция</span>
          </div>
        </div>

        {/* Payment Card - Ethereum */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              Оплата через криптовалюту
            </h2>
            <p className="text-foreground/70">
              Быстрая и безопасная оплата с помощью USDT
            </p>
          </div>

          {/* Ethereum ERC20 Section */}
          <div className="mb-12 pb-12 border-b-2 border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-foreground mb-2">Вариант 1: Ethereum (ERC20)</h3>
            </div>

            {/* QR Code Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="bg-white p-6 rounded-2xl shadow-lg border-4 border-primary/20 mb-4">
                <AppImage
                  src="/assets/images/photo_2025-12-24_14-02-11-1766559754184.jpg"
                  alt="QR код для USDT оплаты на Bybit через сеть Ethereum (ERC20)"
                  className="w-64 h-64 object-contain"
                />
              </div>
              <p className="text-sm text-foreground/60 text-center">
                Отсканируйте QR-код в приложении Bybit для оплаты
              </p>
            </div>

            {/* Payment Details */}
            <div className="space-y-6">
              {/* Network */}
              <div className="p-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground/70">Сеть</span>
                  <div className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                    ERC20
                  </div>
                </div>
                <p className="text-xl font-bold text-foreground">Ethereum (ERC20)</p>
              </div>

              {/* Wallet Address */}
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground/70">Адрес кошелька</span>
                  <button
                    onClick={() => copyToClipboard(ethWalletAddress, setCopiedEth)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    <Icon name={copiedEth ? "Check" : "Copy"} size={16} />
                    {copiedEth ? 'Скопировано!' : 'Копировать'}
                  </button>
                </div>
                <p className="text-sm md:text-base font-mono text-foreground break-all bg-white p-4 rounded-lg border border-gray-300">
                  {ethWalletAddress}
                </p>
              </div>
            </div>
          </div>

          {/* TRON TRC20 Section */}
          <div className="mb-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-foreground mb-2">Вариант 2: TRON (TRC20)</h3>
            </div>

            {/* QR Code Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="bg-white p-6 rounded-2xl shadow-lg border-4 border-green-500/20 mb-4">
                <AppImage
                  src="/assets/images/photo_2025-12-24_14-02-06-1766560035304.jpg"
                  alt="QR код для USDT оплаты на Bybit через сеть TRON (TRC20)"
                  className="w-64 h-64 object-contain"
                />
              </div>
              <p className="text-sm text-foreground/60 text-center">
                Отсканируйте QR-код в приложении Bybit для оплаты
              </p>
            </div>

            {/* Payment Details */}
            <div className="space-y-6">
              {/* Network */}
              <div className="p-6 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground/70">Сеть</span>
                  <div className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                    TRC20
                  </div>
                </div>
                <p className="text-xl font-bold text-foreground">TRON (TRC20)</p>
              </div>

              {/* Wallet Address */}
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground/70">Адрес кошелька</span>
                  <button
                    onClick={() => copyToClipboard(tronWalletAddress, setCopiedTron)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    <Icon name={copiedTron ? "Check" : "Copy"} size={16} />
                    {copiedTron ? 'Скопировано!' : 'Копировать'}
                  </button>
                </div>
                <p className="text-sm md:text-base font-mono text-foreground break-all bg-white p-4 rounded-lg border border-gray-300">
                  {tronWalletAddress}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/20 mb-8">
            <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={20} className="text-primary" />
              Инструкция по оплате
            </h3>
            <ol className="space-y-3 text-foreground/70">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Откройте приложение Bybit и перейдите в раздел "Кошелек"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Выберите USDT и нажмите "Отправить"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Выберите нужную сеть (Ethereum ERC20 или TRON TRC20)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Отсканируйте соответствующий QR-код или вставьте адрес кошелька</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                <span>Введите сумму и подтвердите транзакцию</span>
              </li>
            </ol>
          </div>

          {/* Legal Compliance */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon name="FileText" size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Договор-оферта</h4>
                <p className="text-sm text-foreground/70 mb-4">
                  Совершая оплату, вы соглашаетесь с условиями договора-оферты на оказание информационно-аналитических услуг.
                </p>
                <Button
                  onClick={handleGoToContract}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Icon name="FileText" size={16} />
                  Ознакомиться с договором-офертой
                </Button>
              </div>
            </div>
          </div>

          {/* Security Indicators */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <Icon name="Lock" size={16} className="text-green-600" />
              <span>SSL Защита</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <Icon name="Shield" size={16} className="text-green-600" />
              <span>Blockchain Верификация</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <span>Безопасная транзакция</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleGoHome}
            variant="outline"
            className="flex items-center justify-center gap-2 px-8 py-4 text-lg"
          >
            <Icon name="Home" size={20} />
            Вернуться на главную
          </Button>
          <Button
            onClick={() => window.open('https://wa.me/66823204796?text=Здравствуйте!%20У%20меня%20вопрос%20по%20оплате', '_blank')}
            className="flex items-center justify-center gap-2 px-8 py-4 text-lg bg-green-600 hover:bg-green-700"
          >
            <Icon name="MessageCircle" size={20} />
            Связаться с поддержкой
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PaymentPage;